import React, { useState, useContext, useEffect, useRef } from "react"
import DeleteDialogueModal from "../DialogueModal"
import Loader from "../ui/loader"
import moment from "moment"
import ServiceCardBannerOverlay from "./ServiceCardBannerOverlay"
import useLoading from "../../hooks/useLoading"
import { Badge } from "../ui/badge"
import {
  SvgArchive,
  SvgCheckCircle,
  SvgCopy,
  SvgEdit,
  SvgSettings,
  SvgShare,
  SvgTrash,
} from "../icons/"
import { pluralize } from "../../utils/pluralize"
import { getOrdinal } from "../../utils/get-ordinal"
import { getUnits } from "../../utils/services/get-units/get-units"
import { getSessionType } from "../../utils/services/get-session-type/get-session-type"
import { getSessionTypeImage } from "../../utils/services/get-session-type-image"
import { getPrice } from "../../utils/services/get-price"
import { Label } from "../ui/label"
import { MeetingRequests } from "../../api/app.service"
import { ServiceRequests } from "../../api/app.service"
import { Switch } from "../ui/switch"
import { UserCtx } from "../../context/userContext"
import { useToast } from "../../hooks/useToast"

interface Props {
  service: any
  setUpdateService: any
  getServices: any
  showComponent: any
  setShowArchivedServices: any
  setIsGroupService: any
  setShowDuplicateService?: any
  meetings?: any
}

const ServiceCard: React.FC<Props> = ({
  service,
  setUpdateService,
  getServices,
  showComponent,
  setShowArchivedServices,
  setIsGroupService,
  setShowDuplicateService,
  meetings,
}) => {
  const { user } = useContext(UserCtx)
  const endpointSlug = user?.coachProfile?.endpoint_slug
  const [showDialogueModal, setShowDialogueModal] = useState<boolean>(false)
  const { service_details, is_group_service } = service

  const { toast } = useToast()
  const { startLoading, stopLoading } = useLoading()
  const [loadingToggleChange, setLoadingToggleChange] = useState<boolean>(false)
  const [isServiceVisible, setIsServiceVisible] = useState(
    service_details?.visibility?.profile?.service
  )
  const matchingMeetingIds = (meetings || []).map((m: any) => m.meeting_id)

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showRecurrencePopover, setShowRecurrencePopover] = useState(false)
  const recurrencePopoverRef = useRef<HTMLDivElement>(null)
  const recurrenceTriggerRef = useRef<HTMLButtonElement>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const getSessionCount = () => {
    if (["consultation", "individual"].includes(service_details?.type)) {
      return `${service_details?.session.count} session`
    } else if (["bundle"].includes(service_details?.type)) {
      return pluralize(service_details?.session?.count, "session")
    } else if (["recurring"].includes(service_details?.type)) {
      return (
        <div>
          {pluralize(service_details?.session?.count, "session")}
          <span className="text-darkGray">
            {" "}
            / {`${service_details?.billing?.unit}`}
          </span>
        </div>
      )
    } else {
      return "Not Found"
    }
  }

  const getLengthOrExpiration = () => {
    let expirationString = "No expiration"
    if (
      ["consultation", "individual", "bundle"].includes(service_details?.type)
    ) {
      if (service_details?.expiration?.expires) {
        expirationString = `Expires after ${service_details?.expiration?.time} ${service_details?.expiration?.type}`
      } else {
        expirationString = "No expiration"
      }
    } else if (["recurring"].includes(service_details?.type)) {
      const units =
        service_details?.expiration?.type === "weeks" ? "week" : "month"
      expirationString = `${service_details?.expiration?.time} ${units} program`
    } else {
      expirationString = "Expiration not found"
    }
    return expirationString
  }

  const archiveService = () => {
    setShowDialogueModal(false)
    if (user) {
      startLoading()
      ServiceRequests.archiveService({
        user_id: parseInt(user.user_id),
        stripe_product_id: service.stripe_product_id,
      })
        .then(() => {
          toast.success("Your service was archived!")
          getServices()
          showComponent(setShowArchivedServices)
        })
        .catch((ex) => {
          toast.error(ex.response?.data?.message)
          stopLoading()
        })
    }
  }

  const unarchiveService = () => {
    if (user) {
      startLoading()
      ServiceRequests.unarchiveService({
        user_id: parseInt(user.user_id),
        stripe_product_id: service.stripe_product_id,
      })
        .then(() => {
          toast.success("Your service was unarchived!")
          getServices()
        })
        .catch((ex) => {
          toast.error(ex.response?.data?.message)
          stopLoading()
        })
    }
  }

  const deleteService = async () => {
    if (!user) return
    try {
      startLoading()
      //  if enrolled count is not null, toast error instead of running deleteService which will fail
      if (
        service.service_details.isGroupService &&
        service.enrolled_count !== null
      ) {
        toast.error(
          "Cannot delete service with active clients. Please archive."
        )
        return
      }
      if (
        service.service_details.isGroupService &&
        service.enrolled_count === null
      ) {
        await Promise.all(
          matchingMeetingIds.map((meetingId: number) =>
            MeetingRequests.cancelMeeting({
              coach_user_id: parseInt(user.user_id),
              profile_id: user.activeProfileId,
              first_name: user.firstName,
              last_name: user.lastName,
              meeting_id: meetingId,
              group: service.service_details.isGroupService,
            })
          )
        )
      }

      await ServiceRequests.deleteService({
        user_id: parseInt(user.user_id),
        stripe_product_id: service.stripe_product_id,
      })

      toast.success("Your service was deleted!")
      getServices()
      window.location.reload()
    } catch (ex: any) {
      console.log(ex)
      toast.error(
        ex.response?.data?.error_detail || "Failed to delete service."
      )
    } finally {
      stopLoading()
    }
  }

  const showArchiveModal = () => {
    setShowDialogueModal(true)
  }

  const updateServiceProfileVisibility = () => {
    if (user) {
      setLoadingToggleChange(true)
      const updatedVisibility = !isServiceVisible
      setIsServiceVisible(updatedVisibility)
      const serviceObject = {
        ...service_details,
        visibility: {
          ...service_details.visibility,
          profile: {
            ...service_details.visibility.profile,
            service: updatedVisibility,
          },
        },
        image: {
          ...service_details.image,
          base64: "",
          base64extension: "",
        },
      }
      ServiceRequests.updateClientService({
        user_id: parseInt(user.user_id),
        stripe_product_id: service.stripe_product_id,
        stripe_price_id: service.stripe_price_id,
        service: serviceObject,
        group_service_id: service?.group_service_id,
      })
        .then(() => {
          setLoadingToggleChange(false)
          getServices()
          toast.success(
            updatedVisibility
              ? `Your service, '${service_details?.title}', is now public!`
              : `Your service, '${service_details?.title}', is now private!`
          )
        })
        .catch((ex: any) => {
          toast.error(ex.response?.data?.message)
          stopLoading()
        })
    }
  }

  const copyShareLink = () => {
    const base = process.env.REACT_APP_BASE_URL
    const shareLink = `${base}/find-a-coach/${endpointSlug}/services/${service?.service_id}`
    const freeConsutlationShareLink = `${base}/${endpointSlug}/${service?.service_id}`
    if (
      service?.service_details.type === "consultation" &&
      service?.service_details.billing.amount === "0"
    ) {
      navigator.clipboard.writeText(freeConsutlationShareLink)
    } else {
      navigator.clipboard.writeText(shareLink)
    }
    toast.success("Copied link to clipboard.")
  }

  const getStartDates = (): { startMessage: string } => {
    const meetingDates = service_details?.group?.meetingDates || []
    const userTz = moment.tz.guess()

    const upcomingDates = meetingDates
      .map((date: string, index: number) => {
        const startDate = new Date(date)
        const sessionIndex = index + 1
        const formattedDate = moment
          .tz(startDate, userTz)
          .format("MMM DD [at] h:mm A z")

        return { startDate, sessionIndex, formattedDate }
      })
      .filter((session: any) => session.startDate >= new Date())
      .sort((a: any, b: any) => a.startDate - b.startDate)

    if (upcomingDates.length === 0)
      return { startMessage: "No upcoming sessions" }

    const nextSession = upcomingDates[0]
    const secondSession = upcomingDates[1]

    const isSmallScreen =
      typeof window !== "undefined" && window.innerWidth < 640

    let startMessage = ""
    if (isSmallScreen) {
      if (nextSession) {
        startMessage = `${getOrdinal(nextSession.sessionIndex)}: ${
          nextSession.formattedDate
        }`
      }
      if (secondSession) {
        startMessage = `${getOrdinal(secondSession.sessionIndex)}: ${
          secondSession.formattedDate
        }`
      }
    } else {
      if (nextSession) {
        startMessage = `${getOrdinal(
          nextSession.sessionIndex
        )} Session Scheduled For ${nextSession.formattedDate}`
      }
      if (secondSession) {
        startMessage = `${getOrdinal(
          secondSession.sessionIndex
        )} Session Scheduled For ${secondSession.formattedDate}`
      }
    }

    return { startMessage }
  }

  const getRecurrenceSchedule = (): { recurrenceMessage: string[] } => {
    const weeklyDayTimes = service_details?.group?.weeklyDayTimes || []
    const expireType = service_details?.expiration?.type

    const getDayName = (dayIndex: number): string =>
      moment()
        .isoWeekday(dayIndex === 0 ? 1 : dayIndex + 1)
        .format("dddd")

    const userTz = moment.tz.guess()

    const getTimeWithZone = (time: string): any => {
      const now = moment.tz(userTz)
      const [hour, minute] = time.split(":").map(Number)

      const timeMoment = now.clone().hour(hour).minute(minute).second(0)

      return timeMoment.format("h:mm A z")
    }

    const getMonthlyPrefix = (freq: number): string => {
      const ordinals = ["First", "Second", "Third", "Fourth", "Last"]
      if (freq >= ordinals.length) return "Last"
      return ordinals[freq] || ""
    }

    const scheduleStrings = weeklyDayTimes.map(({ day, freq, time }: any) => {
      const dayName = getDayName(day)
      const formattedTimeWithZone = getTimeWithZone(time)

      if (expireType === "months") {
        const prefix = getMonthlyPrefix(freq)
        return `${prefix} ${dayName}s @ ${formattedTimeWithZone}`
      } else {
        return `${dayName}s @ ${formattedTimeWithZone}`
      }
    })

    return {
      recurrenceMessage: scheduleStrings,
    }
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        recurrencePopoverRef.current &&
        recurrenceTriggerRef.current &&
        !recurrencePopoverRef.current.contains(event.target as Node) &&
        !recurrenceTriggerRef.current.contains(event.target as Node)
      ) {
        setShowRecurrencePopover(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  return (
    <>
      {showDialogueModal && (
        <DeleteDialogueModal
          setShowDialogueModal={setShowDialogueModal}
          confirmCallbackFn={archiveService}
          header="Are you sure?"
          helpText="Once this service is archived, it will be unavailable for members to purchase. It is possible to unarchive the service later. If there are any unused sessions for this Service, they will still be schedulable."
          confirmButtonText="Confirm"
          isWarningVariant={true}
        />
      )}
      <div
        className="w-[350px] sm:w-[540px] lg:w-[504px] main-shadow sm:h-[550px] rounded-[16px] relative flex flex-col cursor-pointer"
        onClick={() => {
          setUpdateService({
            serviceType: service_details?.type,
            show: true,
            service: service,
            source: "MyServices",
          })
          setIsGroupService(service_details?.isGroupService)
        }}
      >
        {" "}
        <div
          className={`w-full h-[120px] rounded-t-[16px] relative ${
            service_details.image.exists ? "" : service_details.image.color
          }`}
          style={{
            backgroundImage: service_details.image.exists
              ? `url(https://clientservice-images-${
                  process.env.REACT_APP_USER_ENVIRONMENT
                }.s3.amazonaws.com/${
                  service?.stripe_product_id
                }?t=${new Date().getTime()})`
              : undefined,
            backgroundSize: "cover",
            backgroundPosition: "center",
            borderRadius: "16px 16px 0 0",
          }}
        >
          <ServiceCardBannerOverlay service={service} />
        </div>
        <div className="mt-4 w-full  flex flex-col sm:px-6 h-[18%]">
          {" "}
          {/* TITLE AND PRICE */}
          <div className="w-full h-[115px] flex flex-col px-2 sm:px-[8px]">
            <div className="flex justify-between items-center]">
              <div
                className={`font-bold break-words flex-1 ${
                  service_details?.title?.length > 73
                    ? "text-[12px]"
                    : "text-[18px]"
                }`}
              >
                {service_details?.title}
              </div>
              <div className="flex flex-col sm:flex-row items-center ml-4">
                <h3 className="font-bold text-lg sm:text-[22px] text-blurple">
                  {getPrice(
                    parseInt(service_details?.billing?.amount),
                    service_details?.symbol
                  )}
                </h3>
                <span
                  className={`text-graySlate text-base font-normal mt-1 sm:ml-2 sm:mt-0 ${
                    ["month", "year", "week"].includes(
                      service_details?.billing.unit
                    )
                      ? "whitespace-nowrap"
                      : "md:whitespace-nowrap"
                  }`}
                >
                  {getUnits(
                    service_details.billing.unit,
                    parseInt(service_details?.billing?.amount),
                    service_details?.type
                  )}
                </span>
              </div>
            </div>

            {is_group_service && (
              <>
                {service?.service_details.type === "recurring"
                  ? (() => {
                      const recurrenceMessages =
                        getRecurrenceSchedule().recurrenceMessage
                      return recurrenceMessages.length > 1 ? (
                        <div className="mt-2">
                          <button
                            ref={recurrenceTriggerRef}
                            className="text-blurple text-sm font-bold"
                            onClick={(e) => {
                              e.stopPropagation()
                              e.preventDefault()
                              setShowRecurrencePopover((prev) => !prev)
                            }}
                          >
                            Click here to see scheduled dates
                          </button>
                          {showRecurrencePopover && (
                            <div
                              ref={recurrencePopoverRef}
                              className="absolute z-10 mt-2 p-3 bg-white border rounded shadow-md whitespace-nowrap"
                            >
                              {recurrenceMessages.map((msg, idx) => (
                                <div
                                  key={idx}
                                  className="mb-1 font-normal text-sm"
                                >
                                  {msg}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="mt-2">
                          <Badge variant="secondary">
                            {recurrenceMessages[0]}
                          </Badge>
                        </div>
                      )
                    })()
                  : getStartDates().startMessage && (
                      <div className="mt-2">
                        <Badge variant="secondary">
                          {getStartDates().startMessage}
                        </Badge>
                      </div>
                    )}
              </>
            )}
          </div>
        </div>
        <div className="w-full h-[1px] bg-grayMist mt-4 sm:mt-0 mb-2 px-5"></div>
        <div className="p-1 sm:px-10">
          <div className="flex items-center justify-between w-full mb-2 text-base font-bold ">
            <div className="w-[142px] h-[32px] bg-grayFlash rounded-[8px] flex items-center justify-center">
              Program Details
            </div>
            <div className="flex items-center gap-[8px]">
              {getSessionTypeImage(service_details.type)}
              {getSessionType(service_details.type)}
            </div>
          </div>
          <div className="flex flex-col gap-[12px] text-base ">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {service?.service_details?.details.features.feature1 ? (
                  <>
                    <SvgCheckCircle />
                    {service?.service_details.details.features.feature1}
                  </>
                ) : (
                  <>
                    <SvgCheckCircle />
                    {getSessionCount()}
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              {service?.service_details?.details.features.feature2 ? (
                <>
                  <SvgCheckCircle />
                  {service?.service_details.details.features.feature2}
                </>
              ) : (
                <>
                  <SvgCheckCircle />
                  <div>
                    {parseInt(service_details?.session?.duration) / 60} minutes
                    <span className="text-darkGray"> / session</span>
                  </div>
                </>
              )}
            </div>

            <div className="flex items-center gap-2">
              {service?.service_details?.details.features.feature3 ? (
                <>
                  <SvgCheckCircle />
                  {service?.service_details.details.features.feature3}
                </>
              ) : (
                <>
                  <SvgCheckCircle />
                  {getLengthOrExpiration()}
                </>
              )}
            </div>
          </div>
        </div>{" "}
        {service?.is_group_service ? (
          <div className="sm:px-10 mt-4 text-base ml-2 mb-2">
            <span className="font-bold">Enrolled Count: </span>{" "}
            <span className="text-blurple ml-2">
              {service?.enrolled_count || 0}
            </span>
          </div>
        ) : (
          <div className="px-10 mt-4 text-base mb-2 invisible">
            <span className="font-bold">Enrolled Count: </span>
            {service?.enrolled_count}
          </div>
        )}
        <div className="relative sm:px-10 flex flex-col sm:gap-4 z-[50]">
          <div className="flex w-full items-center justify-between p-2 rounded">
            <p className="font-bold text-base">Service Visibility</p>
            <div className="flex items-center gap-2">
              <Label htmlFor="service-visible">Private</Label>
              {loadingToggleChange ? (
                <Loader />
              ) : (
                <Switch
                  id="service-visible"
                  onCheckedChange={updateServiceProfileVisibility}
                  checked={isServiceVisible}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
              <Label htmlFor="service-visible">Public</Label>
            </div>
          </div>

          <div className="p-4 sm:p-0 flex items-center justify-between gap-2 mb-2 -mt-4 sm:-mt-0">
            {["active", "inactive"].includes(service_details?.status) ? (
              <>
                <button
                  className="btn-primary btn-secondary gap-2 md:w-[110px]"
                  onClick={(e) => {
                    e.stopPropagation()
                    setUpdateService({
                      serviceType: service_details?.type,
                      show: true,
                      service: service,
                      source: "MyServices",
                    })
                    setIsGroupService(service_details?.isGroupService)
                  }}
                >
                  Edit
                  <SvgEdit />
                </button>

                {service_details?.status === "active" && (
                  <button
                    className="btn-primary btn-secondary gap-2 md:w-[110px]"
                    onClick={(e) => {
                      e.stopPropagation()
                      copyShareLink()
                    }}
                  >
                    Share
                    <SvgShare />
                  </button>
                )}

                <div className="relative z-[50]" ref={dropdownRef}>
                  <button
                    className="btn-primary btn-secondary gap-2 md:w-[110px]"
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsDropdownOpen(!isDropdownOpen)
                    }}
                  >
                    More <SvgSettings fill="black" height={22} />
                  </button>

                  {isDropdownOpen && (
                    <div className="absolute right-0 mt-2 p-2 bg-white border shadow-md rounded-md">
                      <button
                        className="flex items-center gap-2 text-[red] px-3 py-2 hover:bg-gray-100 rounded-md"
                        onClick={(e) => {
                          e.stopPropagation()
                          deleteService()
                        }}
                      >
                        Delete
                        <SvgTrash />
                      </button>
                      {service?.service_details.type !== "consultation" && (
                        <button
                          className="flex items-center gap-2 text-blue-500 px-3 py-2 hover:bg-gray-100 rounded-md"
                          onClick={(e) => {
                            e.stopPropagation()
                            setShowDuplicateService({ show: true, service })
                          }}
                        >
                          Duplicate
                          <SvgCopy />
                        </button>
                      )}
                      <button
                        className="flex items-center gap-2 text-blue-500 px-3 py-2 hover:bg-gray-100 rounded-md"
                        onClick={(e) => {
                          e.stopPropagation()
                          showArchiveModal()
                        }}
                      >
                        Archive
                        <SvgArchive />
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <button
                  className="btn-primary btn-secondary gap-[10px] w-[140px]"
                  onClick={(e) => {
                    e.stopPropagation()
                    unarchiveService()
                  }}
                >
                  Unarchive
                  <SvgArchive />
                </button>
                <button
                  className="btn-primary btn-secondary gap-[10px] w-[140px]"
                  onClick={(e) => {
                    e.stopPropagation()
                    deleteService()
                  }}
                >
                  Delete
                  <SvgTrash />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default ServiceCard
